import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Camera, CameraOff, CheckCircle2, XCircle, CloudOff } from "lucide-react";
import { enqueue, isOffline, getQueue, QUEUE_EVENT } from "@/lib/attQueue";

function CameraReader({ onCode }) {
  const id = useRef(`qr-reader-${Math.random().toString(36).slice(2)}`).current;
  const cb = useRef(onCode);
  const [err, setErr] = useState(null);
  cb.current = onCode;
  useEffect(() => {
    const sc = new Html5Qrcode(id);
    let started = false;
    let last = { t: "", at: 0 };
    sc.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => {
      if (text === last.t && Date.now() - last.at < 4000) return;
      last = { t: text, at: Date.now() };
      cb.current(text);
    }, () => {}).then(() => { started = true; })
      .catch(() => setErr("No pudimos abrir la cámara. Revisa los permisos del navegador o pega el código."));
    return () => { if (started) sc.stop().then(() => sc.clear()).catch(() => {}); };
  }, [id]);
  return (
    <div>
      <div id={id} className="w-full max-w-xs mx-auto rounded-2xl overflow-hidden bg-black/5" data-testid="qr-camera" />
      {err && <p className="text-xs text-destructive mt-2" data-testid="qr-camera-error">{err}</p>}
    </div>
  );
}

function ScanResult({ result }) {
  const ok = result.valid;
  if (result.queued) return (
    <Card className="p-4 text-center border-amber-400 bg-amber-50 text-amber-900" data-testid="qr-result-queued">
      <CloudOff className="w-9 h-9 mx-auto" />
      <p className="font-bold mt-2">QR guardado sin señal</p>
      <p className="text-xs mt-1">Se validará y registrará con la hora del escaneo apenas vuelva la conexión.</p>
    </Card>
  );
  return (
    <Card className={`p-4 text-center ${ok ? "border-[hsl(145,42%,38%)]" : "border-destructive"}`} data-testid="qr-result">
      {ok ? <CheckCircle2 className="w-9 h-9 text-[hsl(145,42%,38%)] mx-auto" /> : <XCircle className="w-9 h-9 text-destructive mx-auto" />}
      <p className="font-bold mt-2" data-testid="qr-result-reason">{result.reason}</p>
      {result.reservation && (
        <p className="text-sm text-muted-foreground mt-1" data-testid="qr-result-student">
          {result.reservation.customer_name} · {result.reservation.workshop_name} · {result.reservation.quantity} cupo(s)
        </p>
      )}
      {result.session && (
        <p className="text-xs text-muted-foreground mt-0.5" data-testid="qr-result-session">
          Sesión {result.session.number} de {result.session.total} · {new Date(result.session.starts_at).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })} hrs
        </p>
      )}
    </Card>
  );
}

export function QrCheckin({ onScanned }) {
  const [code, setCode] = useState("");
  const [camera, setCamera] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    const h = () => setResult((r) => (r?.queued && !getQueue().some((q) => q.type === "scan") ? null : r));
    window.addEventListener(QUEUE_EVENT, h);
    return () => window.removeEventListener(QUEUE_EVENT, h);
  }, []);
  const scan = async (raw) => {
    const value = (raw ?? code).trim();
    if (!value) return;
    const queue = () => { enqueue({ type: "scan", codigo_qr: value }); setResult({ queued: true }); toast.info("Sin señal: QR guardado. Se registrará al volver la conexión."); };
    if (!navigator.onLine) { queue(); setCode(""); return; }
    try {
      const { data } = await api.post("/business/scan", { codigo_qr: value }, { offlineFallback: true });
      setResult(data);
      if (data.valid) { toast.success(data.reason); onScanned?.(); } else toast.error(data.reason);
    } catch (e) { if (isOffline(e)) queue(); else setResult({ valid: false, reason: formatApiErrorDetail(e.response?.data?.detail) }); }
    setCode("");
  };
  return (
    <div className="space-y-4" data-testid="qr-checkin">
      <Button variant={camera ? "outline" : "default"} className="w-full" onClick={() => setCamera((c) => !c)} data-testid="qr-camera-toggle">
        {camera ? <><CameraOff className="w-4 h-4 mr-1.5" />Cerrar cámara</> : <><Camera className="w-4 h-4 mr-1.5" />Escanear con la cámara</>}
      </Button>
      {camera && <CameraReader onCode={scan} />}
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="O pega el código del ticket"
          className="font-mono" data-testid="qr-input" onKeyDown={(e) => e.key === "Enter" && scan()} />
        <Button variant="secondary" onClick={() => scan()} data-testid="qr-scan-btn">Validar</Button>
      </div>
      {result && <ScanResult result={result} />}
    </div>
  );
}
